import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { useMedia } from '@tager/web-core';

import { colors } from '@/constants/theme';
import PlusIcon from '@/assets/svg/plus.svg';
import MinusIcon from '@/assets/svg/minus.svg';
import DropdownListPicker from '@/components/Picker/DropdownListPicker';
import { media } from '@/utils/mixin';
import { getCreditPeriods } from '@/utils/common';

import { RangeStyles } from './styles';
import { PeriodInputProps } from './PeriodInput.types';

const getPeriodValues = (min: number, max: number): string[] => {
  let startPeriod = min;
  let periodValues: string[] = [];

  while (startPeriod <= max) {
    periodValues.push(String(startPeriod));
    startPeriod++;
  }

  return periodValues;
};

export default function PeriodInput({
  min,
  max,
  value,
  onChange,
  badge,
  options,
}: PeriodInputProps) {
  const progress = 100 * ((value - min) / (max - min));
  const progressRange = progress > 100 ? 100 : progress;
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const isTablet = useMedia(`(max-width: 1259px)`);

  const legendValues = getCreditPeriods(min, max);
  const legendValuesLength = legendValues.length;

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChange?.(Number(value));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    function isKeyAllowed(key: string) {
      if (key >= '0' && key <= '9') {
        return true;
      }

      return ['ArrowLeft', 'ArrowRight', 'Delete', 'Backspace'].includes(key);
    }

    if (event.ctrlKey || event.metaKey) {
      return true;
    }

    if (!isKeyAllowed(event.key)) {
      event.preventDefault();
    }
  };

  const handleIncrease = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (value < max) {
      onChange?.(Number(value) + 1);
      event.stopPropagation();
    }
  };

  const handleDecrease = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (value > min) {
      onChange?.(Number(value) - 1);
      event.stopPropagation();
    }
  };

  const handlePeriodInputClick = () => {
    if (isTablet) {
      setDropdownVisible(!isDropdownVisible);
    } else {
      return;
    }
  };

  return (
    <Component>
      <Label>{!isTablet ? 'Срок погашения' : 'Срок погашения, мес'}</Label>
      <InputContainer onClick={handlePeriodInputClick}>
        <Input
          readOnly
          type="text"
          min={min}
          max={max}
          value={value}
          onChange={(event) => handleChangeValue(event)}
          onKeyDown={handleKeyDown}
          isWhiteBackground={options?.isWhiteBackground}
        />
        {badge && !isTablet && <Badge>{badge}</Badge>}
        {isTablet && (
          <Buttons>
            <CalcButton dec onClick={handleDecrease} disabled={value === min}>
              <MinusIcon />
            </CalcButton>

            <CalcButton inc onClick={handleIncrease} disabled={value === max}>
              <PlusIcon />
            </CalcButton>
          </Buttons>
        )}

        {!isTablet && (
          <DraggableInput
            type="range"
            min={min}
            max={max}
            value={value}
            onChange={(event) => handleChangeValue(event)}
          />
        )}

        {!isTablet && <Progress progressRange={progressRange} />}
        {isTablet && isDropdownVisible && (
          <StyledDropdownListPicker
            activeValue={String(value)}
            list={getPeriodValues(min, max)}
            onClick={(value) => {
              onChange?.(Number(value));
            }}
            onClose={() => setDropdownVisible(false)}
          />
        )}
      </InputContainer>
      {!isTablet && (
        <Legend>
          <LegendInner legendValuesLength={legendValuesLength}>
            {legendValues.map((value, index) => (
              <LegendItem key={index} legendValuesLength={legendValuesLength}>
                {value}
                {index > 0 && <LegendItemLine />}
              </LegendItem>
            ))}
          </LegendInner>
        </Legend>
      )}
    </Component>
  );
}

const Component = styled.div`
  display: block;
  width: 100%;
`;

const Label = styled.div`
  font-size: 12px;
  line-height: 14px;
  text-transform: uppercase;
  color: ${(props) => props.theme.black200_alpha40};
`;

const InputContainer = styled.div`
  position: relative;
  margin-top: 6px;

  ${RangeStyles};
`;

const Input = styled.input<{
  isWhiteBackground?: boolean;
}>`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 80px 10px 10px;
  background: ${(props) => props.theme.white_1};
  border: 1px solid ${(props) => props.theme.black200_alpha40_1};
  box-sizing: border-box;
  border-radius: 5px;
  width: 100%;
  height: 42px;
  caret-color: transparent;
  color: ${(props) => props.theme.black};

  ${media.tablet(css`
    text-align: center;
    padding: 10px;
  `)}

  ${({ isWhiteBackground }) =>
    isWhiteBackground &&
    css`
      border: 1px solid ${(props) => props.theme.white_1};
    `}
`;

const Badge = styled.span`
  position: absolute;
  top: 11.5px;
  right: 10px;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: ${(props) => props.theme.black200_alpha24};
`;

const Buttons = styled.div`
  display: flex;
  height: 100%;
  position: absolute;
  top: 0;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  right: 0;
  padding: 0 47px;

  ${media.tablet(
    css`
      padding: 0;
    `
  )}
`;

const CalcButton = styled.button<{
  inc?: boolean;
  dec?: boolean;
}>`
  margin: 0;
  display: flex;
  align-items: center;

  ${media.tablet(
    css`
      padding: 0 15%;
    `
  )}

  &:disabled {
    opacity: 0.3;
  }

  & > svg {
    width: 12px;
    fill: ${(props) => props.theme.black100};

    rect {
      fill: ${(props) => props.theme.black100};
    }

    path {
      fill: ${(props) => props.theme.black100};
    }
  }
`;

const DraggableInput = styled.input``;

const Progress = styled.div<{
  progressRange: number;
}>`
  height: 4px;
  position: absolute;
  bottom: 0;
  background: ${colors.main};
  pointer-events: none;

  ${({ progressRange }) =>
    progressRange &&
    css`
      width: ${progressRange}%;
    `}
`;

const Legend = styled.div`
  width: 100%;
  margin-top: 7px;
`;

const LegendInner = styled.div<{ legendValuesLength: number }>`
  display: flex;

  ${({ legendValuesLength }) => css`
    transform: translateX(-${100 / legendValuesLength / 2}%);
    width: ${100 + 100 / legendValuesLength}%;
  `}
`;

const LegendItem = styled.div<{ legendValuesLength: number }>`
  position: relative;
  display: flex;
  justify-content: center;

  font-size: 14px;
  line-height: 16px;
  color: ${(props) => props.theme.black200_alpha40};

  &:not(:first-child, :last-child) {
    font-weight: 300;
    font-size: 12px;
    line-height: 14px;
  }

  ${({ legendValuesLength }) => css`
    width: ${100 / legendValuesLength}%;
  `}
`;

const LegendItemLine = styled.div`
  position: absolute;
  width: 1px;
  height: 7px;
  left: 0;
  border-left: 1px solid ${(props) => props.theme.black200_alpha40};
`;

const StyledDropdownListPicker = styled(DropdownListPicker)`
  position: absolute;
  margin-top: 5px;
`;
